import { CancellationToken } from "../../../../base/common/cancellation";
import { createDecorator } from "../../../../platform/instantiation/common/registry";
import { Event } from "../../../../base/common/events";
import { IDisposable } from "../../../../base/common/lifecycle";
import { StandardMouseEvent } from "../../../../base/browser/mouseEvent";
import { ServicesAccessor } from "../../../../platform/instantiation/common/instantiation";
import { VSBuffer } from "../../../../base/common/buffer";
import { URI } from "../../../../base/common/uri";
import { IProperty } from "../../../../platform/properties/common/properties";


// #region IAssetsService

export const IAssetsService = createDecorator<IAssetsService>("assetsService");
export interface IAssetsService {

    readonly _serviceBrand: undefined;

    readonly onDidChange: Event<void>;

    queryOptions(): Promise<AssetOptionsResult>;

    list(options: AssetFetchOptions, token?: CancellationToken, onProgress?: (progress: AssetFetchProgress) => void): Promise<AssetFetchResult>;

    loadAsset(id: string, token?: CancellationToken): Promise<FullAsset | null>;

    registerInteractionHandler(handler: IAssetInteractionHandler): IDisposable;

    /**
     * Handles the given asset interaction by delegating to registered interaction handlers.
     * @returns a promise that resolves to true if the interaction was handled, false otherwise. If no handler was found, null is returned.
     */
    handleInteraction(interaction: AssetInteraction, token?: CancellationToken): Promise<boolean> | null;

}

export type AssetType = "texture" | "model" | "hdr" | "material";

// AssetType const array
export const AssetTypes: readonly AssetType[] = ["texture", "model", "hdr", "material"];

export function assetTypeDisplayName(type: AssetType | ({} & string)): string {
    switch (type) {
        case "texture": return "Texture";
        case "model": return "3D Model";
        case "hdr": return "HDRI";
        case "material": return "Material";
    }
    return type;
}

export interface Asset {
    readonly type: AssetType;
    readonly id: string;
    readonly provider?: string;
    mime?: string;
    category?: string;
    tags?: string[];
    name?: string;
    description?: string;
    thumbnailUrl?: string;
    thumbnailResolution?: { width: number; height: number };
    createdAt?: number;
    updatedAt?: number;
    externalLink?: string;
}

export interface AssetOptionsResult {
    types: AssetType[];
    categories?: { [type in AssetType]?: string[] };
    tags?: { [type in AssetType]?: string[] };
}

export interface AssetFetchOptions {
    type: AssetType;
    category?: string[];
    tags?: string[];
}

export interface AssetFetchResult {
    assets: Asset[];
}

export interface AssetFetchProgress {

}



export interface FullAsset {
    readonly id: string;
    readonly versions: AssetVersion[];
}
export interface AssetVersion {
    /**
     * URI to fetch the asset (e.g. download link or file path)
     */
    uri?: URI;

    /**
     * Raw data of the asset version (if already loaded)
     */
    data?: VSBuffer;

    /**
     * Version string, e.g. "1.0.0"
     */
    version?: string;
    /**
     * Optional content MD5 hash for integrity check
     */
    contentMd5?: string;
    /**
     * File size in bytes
     */
    fileSize?: number;
    /**
     * Additional metadata depending on asset type
     */
    resolution?: "1k" | "2k" | "4k" | "8k" | "16k" | number | { width: number; height: number };
    /**
     * Tags associated with this version
     */
    tags?: string[];
    /**
     * File format, e.g. "jpg", "png", "hdr", "exr"
     */
    format?: "gltf" | "jpg" | "png" | "hdr" | "exr" | ({} & string);

    resourcePath?: string;

    resolveUrl?: (url: string) => string;

    /**
     * Total size of all related files (e.g. for glTF with external resources)
     */
    totalSize?: number;
}

// #region AssetOpener

export type AssetInteraction = {
    type: ({} & string);
    assets: Asset[];
    targets?: unknown[];
    noTelemetry?: boolean;
    position?: any;
}  & (
        {
            /** Dragdrop */
            type: "dragdrop";
            position: { x: number; y: number };
        } |
        {
            /** Use selection */
            type: "selected";
        } | 
        {
            type: "targets";
            /** Target objects, can be a property or a 3D object in the scene that should receive e.g. the material */
            targets: Array<IProperty | object>;
        }
    )

export function isDragDropInteraction(interaction: AssetInteraction): boolean {
    return interaction.type === "dragdrop";
}

/**
 * Checks if the given interaction is a "select" interaction, e.g. when clicking an asset in the asset browser
 */
export function isSelectionInteraction(interaction: AssetInteraction): boolean {
    return interaction.type === "selected";
}

export function isTargetsInteraction(interaction: AssetInteraction): boolean {
    return interaction.type === "targets";
}

export interface IAssetInteractionHandler {
    canOpenAsset(accessor: ServicesAccessor, interaction: AssetInteraction): boolean;
    openAsset(accessor: ServicesAccessor, interaction: AssetInteraction, token?: CancellationToken): Promise<boolean>;
}